/**
 * Dashboard workspace tab — the S2 shell. Resolves the tab's dashboardId
 * (falling back to the most recently updated dashboard, or the zero state),
 * and composes toolbar + grid + settings dialog. All data flows through the
 * S1 store mirror of the main-process repository.
 */
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { isDashboardTab } from '../../core/types/workspaceTypes';
import type { WorkspaceTabComponentProps } from '../../core/types/workspaceTypes';
import {
  FREE_DASHBOARD_LIMIT,
  isQueryBackedTile,
  type DashboardTile,
} from '../types/dashboardTypes';
import { useDashboardStore } from '../store/dashboardStore';
import { useDashboardRuntimeStore } from '../store/dashboardRuntimeStore';
import { useDashboardActions } from '../hooks/useDashboardActions';
import { useDashboardScheduler } from '../hooks/useDashboardScheduler';
import { useDashboardPresent } from '../hooks/useDashboardPresent';
import { useDashboardParamStore } from '../store/dashboardParamStore';
import { useTileConfigureBridgeStore } from '../store/tileConfigureBridgeStore';
import { DashboardToolbar } from './DashboardToolbar';
import { DashboardGrid } from './DashboardGrid';
import { PresentMode } from './PresentMode';
import { ParameterChipsBar } from './params/ParameterChipsBar';
import { CrossFilterChips } from './cross-filter/CrossFilterChips';
import { FilterWiringPrompt } from './cross-filter/FilterWiringPrompt';
import { TileQueryMenuItems } from './cross-filter/TileQueryMenuItems';
import { StarterDashboards } from './StarterDashboards';
import { DashboardSettingsDialog } from './DashboardSettingsDialog';
import { AddTileDialog } from './add-tile/AddTileDialog';
import { TileDangerousConfirmHost } from './TileDangerousConfirmHost';
import { BuildWithAiPanel } from './BuildWithAiPanel';
import { AlertRuleDialog } from './AlertRuleDialog';
import { HealthRail } from './HealthRail';
import { useDashboardExportFlow } from './export/useDashboardExportFlow';
import { DashboardExportMenuItems } from './export/DashboardExportMenuItems';
import { DashboardExportDialogs } from './export/DashboardExportDialogs';
import { TileDataExportMenuItems } from './export/TileDataExportMenuItems';
import { ChartImageExportMenuItems } from './export/ChartImageExportMenuItems';
import { ScheduleTrustMenuItem } from './ScheduleTrustMenuItem';

const EMPTY_TILES: DashboardTile[] = [];

export function DashboardTab({ tabId, className }: WorkspaceTabComponentProps) {
  const tab = useWorkspaceStore((s) => s.tabs.find((t) => t.id === tabId));
  const updateTabContent = useWorkspaceStore((s) => s.updateTabContent);
  const updateTabTitle = useWorkspaceStore((s) => s.updateTabTitle);
  const dashboards = useDashboardStore((s) => s.dashboards);
  const loaded = useDashboardStore((s) => s.loaded);
  const freeLimit = useDashboardStore((s) => s.freeLimit);
  const clearFreeLimit = useDashboardStore((s) => s.clearFreeLimit);
  const load = useDashboardStore((s) => s.load);
  const { track } = useAnalytics();

  const isDashTab = !!tab && isDashboardTab(tab);
  const isTabActive = useWorkspaceStore((s) => s.activeTabId === tabId);
  const dashboardId = tab && isDashboardTab(tab) ? tab.content.dashboardId : null;
  const tabTitle = tab?.title;
  const dashboard = dashboards.find((d) => d.id === dashboardId) ?? null;
  const actions = useDashboardActions(tabId, dashboard);
  const present = useDashboardPresent(dashboard, dashboards, isTabActive);
  const exportFlow = useDashboardExportFlow(dashboard, actions.showTo);

  // While presenting a playlist, the SHOWN dashboard cycles away from the
  // tab's own pointer — the scheduler must follow whichever is on screen,
  // and register the on-deck dashboard's tiles for S13 pre-warm eligibility.
  const scheduledDashboard = present.presenting ? present.shownDashboard : dashboard;
  const scheduler = useDashboardScheduler(
    scheduledDashboard?.id ?? null,
    scheduledDashboard?.tiles ?? EMPTY_TILES,
    isTabActive,
    present.presenting ? (present.onDeckDashboard?.id ?? null) : null,
    present.presenting ? (present.onDeckDashboard?.tiles ?? EMPTY_TILES) : EMPTY_TILES,
  );

  const [editMode, setEditMode] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [tileDialog, setTileDialog] = useState<{ open: boolean; editTile: DashboardTile | null }>({
    open: false,
    editTile: null,
  });
  const [alertDialogTile, setAlertDialogTile] = useState<Extract<DashboardTile, { tileType: 'stat' }> | null>(
    null,
  );
  // D2 — tile pending a schedule-trust confirmation (write-classified interval).
  const [trustTile, setTrustTile] = useState<DashboardTile | null>(null);

  useEffect(() => {
    if (!loaded) void load();
  }, [loaded, load]);

  // Restore persisted parameter selections BEFORE tiles run — useTileResult
  // gates its first fetch on this hydration (no defaults-then-restored churn).
  const shownDashboardId = dashboard?.id ?? null;
  useEffect(() => {
    if (shownDashboardId) void useDashboardParamStore.getState().hydrate(shownDashboardId);
  }, [shownDashboardId]);

  // Resolve a missing/dangling dashboardId to the most recently updated
  // dashboard (toolbar behavior: "most-recent dashboard, or zero state").
  // Guard on an actual change — an unconditional updateTabContent rebuilds
  // the tabs array every effect run and loops (React #185).
  useEffect(() => {
    if (!loaded || !isDashTab || dashboard) return;
    const recent = [...dashboards].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
    const targetId = recent?.id ?? null;
    if (targetId !== dashboardId) {
      updateTabContent<'dashboard'>(tabId, { dashboardId: targetId });
    }
  }, [loaded, isDashTab, dashboard, dashboards, dashboardId, tabId, updateTabContent]);

  // Keep the tab title in sync with the shown dashboard's name (change-guarded).
  useEffect(() => {
    if (!isDashTab) return;
    const desired = dashboard ? dashboard.name : 'Dashboards';
    if (tabTitle !== desired) updateTabTitle(tabId, desired);
  }, [isDashTab, tabId, dashboard, tabTitle, updateTabTitle]);

  // Register this dashboard's Configure handler on the tile-configure bridge —
  // the error tile body's "Configure…" action (TileStateActions) only renders
  // once a handler is registered, and opens the SAME edit-mode AddTileDialog
  // as the kebab's "Configure…" item below. Keyed on dashboardId only — the
  // handler closure itself never changes shape, so a re-registration on every
  // unrelated `dashboard` object update (tiles, runtime, etc.) is wasted work.
  useEffect(() => {
    if (!dashboard) return;
    return useTileConfigureBridgeStore
      .getState()
      .register(dashboard.id, (tile) => setTileDialog({ open: true, editTile: tile }));
  }, [dashboard?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleEdit = () => {
    setEditMode((prev) => {
      track('feature:dashboard_edit_mode', { enabled: !prev });
      return !prev;
    });
  };

  // Toggle-both-ways (repo analytics rule) — the "Build with AI" slide-over
  // had zero instrumentation (S16 sweep finding): open originates from the
  // toolbar button (outside `BuildWithAiPanel`), close from its own "X"
  // button, so both are tracked at this single shared state setter instead
  // of splitting the event between two components.
  const toggleAiPanel = (nextOpen: boolean) => {
    track('feature:dashboard_ai_panel', { enabled: nextOpen });
    setAiPanelOpen(nextOpen);
  };

  if (!loaded) {
    return (
      <div className={`flex h-full items-center justify-center ${className ?? ''}`}>
        <RandomDotmSpinner size={32} />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className={`flex h-full flex-col ${className ?? ''}`} data-testid="dashboard-tab">
        <StarterDashboards
          onDashboardCreated={(id, opts) => {
            actions.showTo(id);
            if (opts?.openAddTile) setTileDialog({ open: true, editTile: null });
          }}
        />
      </div>
    );
  }

  if (present.presenting && present.shownDashboard) {
    return (
      <div className={`flex h-full flex-col ${className ?? ''}`} data-testid="dashboard-tab">
        <PresentMode
          dashboard={present.shownDashboard}
          onDeckDashboard={present.onDeckDashboard}
          index={present.index}
          total={present.total}
          onExit={present.exitPresent}
        />
      </div>
    );
  }

  return (
    <div
      className={`flex h-full flex-col ${className ?? ''}`}
      data-testid="dashboard-tab"
      data-dashboard-capture={dashboard.id}
    >
      <DashboardToolbar
        dashboard={dashboard}
        dashboards={dashboards}
        editMode={editMode}
        onToggleEdit={toggleEdit}
        onSelect={actions.showTo}
        onCreate={actions.createDashboard}
        onAddTile={() => setTileDialog({ open: true, editTile: null })}
        onOpenAiPanel={() => toggleAiPanel(true)}
        onDuplicate={actions.duplicateDashboard}
        onOpenSettings={() => setSettingsOpen(true)}
        onDelete={() => setDeleteOpen(true)}
        onRefreshAll={() => {
          track('feature:dashboard_refresh_all', { tiles: dashboard.tiles.filter(isQueryBackedTile).length });
          scheduler.refreshAll(dashboard.id);
        }}
        refreshAllDisabled={!dashboard.tiles.some(isQueryBackedTile)}
        onBulkRefreshOverride={actions.bulkOverrideRefresh}
        onPresent={present.enterPresent}
        extraKebabItems={<DashboardExportMenuItems flow={exportFlow} />}
      />
      {freeLimit && (
        <div
          className="flex items-center gap-2 border-b border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-xs text-amber-700 dark:text-amber-400"
          data-testid="dashboard-free-limit-banner"
        >
          <span className="flex-1">
            The free plan includes {FREE_DASHBOARD_LIMIT} dashboards. Upgrade to Pro for unlimited
            dashboards.
          </span>
          <button
            onClick={clearFreeLimit}
            className="rounded-sm p-0.5 hover:bg-amber-500/20"
            title="Dismiss"
            aria-label="Dismiss free-limit notice"
            data-testid="dashboard-free-limit-dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <ParameterChipsBar dashboard={dashboard} />
      <CrossFilterChips dashboard={dashboard} />
      <FilterWiringPrompt dashboard={dashboard} />
      {/* overflow-hidden: the closed BuildWithAiPanel sits at translate-x-full,
          and transforms EXTEND the scrollable overflow of the nearest scrolling
          ancestor — without the clip, every dashboard shows a permanent 390px
          horizontal scrollbar at the workspace bottom (S24 F39). The panel
          slides in from this clipped edge, so the animation is unaffected. */}
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <HealthRail dashboardId={dashboard.id} tiles={dashboard.tiles} />
        <DashboardGrid
          dashboardId={dashboard.id}
          tiles={dashboard.tiles}
          editMode={editMode}
          onLayoutCommit={actions.commitLayout}
          onRemoveTile={(tileId) => {
            // Clear BEFORE removal: an in-flight settle for this tile must
            // find no entry and write nothing (S3 guard).
            useDashboardRuntimeStore.getState().clearTile(dashboard.id, tileId);
            actions.removeTile(tileId);
          }}
          onDuplicateTile={actions.duplicateTile}
          tileMenuItems={(tile) => (
            <>
              <DropdownMenuItem
                onClick={() => setTileDialog({ open: true, editTile: tile })}
                data-testid="tile-configure-item"
              >
                Configure…
              </DropdownMenuItem>
              {isQueryBackedTile(tile) && (
                <>
                  <DropdownMenuItem
                    onClick={() => void actions.refreshTile(tile)}
                    data-testid="tile-refresh-item"
                  >
                    Refresh now
                  </DropdownMenuItem>
                  <ScheduleTrustMenuItem
                    dashboardId={dashboard.id}
                    tile={tile}
                    onRequestTrust={() => setTrustTile(tile)}
                  />
                  <TileQueryMenuItems dashboardId={dashboard.id} tile={tile} />
                  <TileDataExportMenuItems dashboardId={dashboard.id} tile={tile} />
                  {tile.tileType === 'chart' && (
                    <ChartImageExportMenuItems dashboardId={dashboard.id} tile={tile} />
                  )}
                </>
              )}
              {tile.tileType === 'stat' && (
                <DropdownMenuItem
                  onClick={() => setAlertDialogTile(tile)}
                  data-testid="tile-alert-rule-item"
                >
                  Alert rule…
                </DropdownMenuItem>
              )}
            </>
          )}
        />
        <BuildWithAiPanel dashboard={dashboard} open={aiPanelOpen} onOpenChange={toggleAiPanel} />
      </div>
      <AddTileDialog
        open={tileDialog.open}
        onOpenChange={(open) => setTileDialog((prev) => ({ ...prev, open }))}
        dashboard={dashboard}
        editTile={tileDialog.editTile}
        onSubmit={actions.submitTile}
      />
      <AlertRuleDialog
        dashboard={dashboard}
        tile={alertDialogTile}
        open={alertDialogTile !== null}
        onOpenChange={(open) => !open && setAlertDialogTile(null)}
        onSave={(tile, alertRule) => void actions.updateAlertRule(tile, alertRule)}
      />
      <TileDangerousConfirmHost active={isTabActive} />
      <DashboardExportDialogs flow={exportFlow} />
      <DashboardSettingsDialog
        dashboard={dashboard}
        dashboards={dashboards}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onRename={actions.renameDashboard}
        onSaveParameters={actions.saveParameters}
        onSavePlaylist={actions.savePlaylist}
      />
      <ConfirmDialog
        open={trustTile !== null}
        onOpenChange={(open) => !open && setTrustTile(null)}
        title="Allow scheduled refresh for this tile?"
        description="This tile's query isn't read-only, so Jam blocks it from running on a schedule. Allow it to run automatically at the tile's refresh interval? Editing the query later revokes this."
        confirmLabel="Allow"
        onConfirm={() => {
          if (trustTile) void actions.grantScheduleTrust(trustTile);
        }}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete “${dashboard.name}”?`}
        description="This removes the dashboard definition from this machine. Tiles and their queries are not recoverable."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={actions.deleteDashboard}
      />
    </div>
  );
}
